import { useEffect, useId, useMemo, useRef, useState } from "react";
import { TextField } from "@/components/TextField";
import { SelectField } from "@/components/SelectField";
import { US_STATE_OPTIONS } from "@/lib/usStates";
import type { AddressValue } from "@/lib/address";
import {
  defaultPlacesClient,
  PlacesSession,
  MIN_QUERY_LENGTH,
  DEBOUNCE_MS,
  type PlaceSuggestion,
  type PlacesClient,
} from "@/lib/placesClient";

// The ONE shared US-address input (BRK-S067) — street + city + state (2-letter
// USPS select) + ZIP. Extracted from the blocks inlined in AccountForm (legal +
// mailing) and reused by BrokerageCard, so the field set, required markers, and
// state dropdown live in one place (DRY). Controlled: the parent owns the value
// and every field stays editable.
//
// When a Google Places client is available (a browser key is configured, or the
// dev-mock path is active), the STREET field becomes a headless autocomplete —
// suggestions in our own dropdown (matching BRK's typeahead idiom), never
// Google's embedded widget. With no client it is exactly today's manual field
// (no autocomplete, no errors, no console noise). See the shared UX contract:
// pms-local docs/address-autocomplete.md.

export function AddressFieldset({
  title,
  value,
  onChange,
  bare = false,
  placesClient,
  error,
  groupLabel,
}: {
  /** Optional heading rendered above the fields (omit for a bare, headingless
   *  block embedded in a larger form, e.g. BrokerageCard). */
  title?: string;
  value: AddressValue;
  onChange: (a: AddressValue) => void;
  /** Drop the card chrome (border/background/padding) — for an address nested
   *  inside another card. */
  bare?: boolean;
  /** Places client override. `undefined` ⇒ resolve the default from config;
   *  `null` ⇒ force the manual-only field. Primarily a test seam. */
  placesClient?: PlacesClient | null;
  /**
   * A validation message for the address AS A WHOLE (BRK-S102). An address is
   * four inputs under one label, so a server reason about "the address" belongs
   * to the GROUP — announced to anyone inside it — rather than being pinned on
   * an arbitrary member like `street`.
   */
  error?: string;
  /** Accessible name for the group, when `title` isn't rendered (bare mode).
   *  Needed so the described group has a name to be announced with. */
  groupLabel?: string;
}) {
  const groupId = useId();
  const client = useMemo(
    () => (placesClient !== undefined ? placesClient : defaultPlacesClient()),
    [placesClient],
  );

  const fields = (
    <>
      {title && <h2 className="font-semibold">{title}</h2>}
      {client ? (
        <AutocompleteStreet value={value} onChange={onChange} client={client} />
      ) : (
        <TextField
          label="Street"
          value={value.street}
          onChange={(e) => onChange({ ...value, street: e.target.value })}
          required
        />
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <TextField
          label="City"
          value={value.city}
          onChange={(e) => onChange({ ...value, city: e.target.value })}
          required
        />
        <SelectField
          label="State"
          placeholder="Select"
          options={US_STATE_OPTIONS}
          value={value.state}
          onChange={(e) => onChange({ ...value, state: e.target.value })}
          required
        />
        <TextField
          label="ZIP"
          value={value.zip}
          onChange={(e) => onChange({ ...value, zip: e.target.value })}
          required
        />
      </div>
    </>
  );
  // An address error describes the whole block, so it rides on a named group and
  // is wired with aria-describedby — visible-but-unannounced is the failure mode
  // BRK-S102 was filed for.
  const errorId = error ? `${groupId}-address-error` : undefined;
  const groupProps = {
    role: "group" as const,
    "aria-label": groupLabel ?? title,
    "aria-invalid": error ? (true as const) : undefined,
    "aria-describedby": errorId,
  };
  const message = error ? (
    <p id={errorId} className="text-sm text-brk-danger">
      {error}
    </p>
  ) : null;
  if (bare)
    return (
      <div className="flex flex-col gap-4" {...groupProps}>
        {fields}
        {message}
      </div>
    );
  return (
    <div
      className="flex flex-col gap-4 rounded-lg border border-brk-border bg-brk-panel p-5"
      {...groupProps}
    >
      {fields}
      {message}
    </div>
  );
}

// The street field with Places autocomplete. Renders the shared TextField plus
// an in-flow suggestion dropdown (in-flow, like ClassCodeField, so it never
// overflows at 375px). Mounted only when a client exists.
function AutocompleteStreet({
  value,
  onChange,
  client,
}: {
  value: AddressValue;
  onChange: (a: AddressValue) => void;
  client: PlacesClient;
}) {
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[] | null>(
    null,
  );
  // "Enter address manually" collapses autocomplete ENTIRELY for the life of
  // the field — no more Places requests (shared contract).
  const [manual, setManual] = useState(false);
  // Skip one query right after a programmatic fill (a selection sets the full
  // street), so choosing a suggestion doesn't immediately re-query it.
  const [suppress, setSuppress] = useState(false);
  // The current billing session — one token spans this focus-session and is
  // consumed by the Place Details call; discarded after a selection so the next
  // focus-session mints a fresh token.
  const sessionRef = useRef<PlacesSession | null>(null);

  // Debounced suggestion fetch: only while focused, not in manual mode, at or
  // above the min length, and not suppressed by a just-completed selection.
  useEffect(() => {
    if (manual || !focused || suppress) return;
    const q = value.street.trim();
    if (q.length < MIN_QUERY_LENGTH) {
      setSuggestions(null);
      setOpen(false);
      return;
    }
    let cancelled = false;
    const ctrl = new AbortController();
    setLoading(true);
    const timer = setTimeout(async () => {
      // Created on the first qualifying keystroke; reused for the whole session.
      sessionRef.current ??= new PlacesSession();
      try {
        const items = await sessionRef.current.suggest(client, q, ctrl.signal);
        if (!cancelled) {
          setSuggestions(items);
          setOpen(true);
        }
      } catch {
        // Handled state, never a throw or console noise: an empty dropdown.
        if (!cancelled) {
          setSuggestions([]);
          setOpen(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      ctrl.abort();
      clearTimeout(timer);
    };
  }, [value.street, focused, manual, suppress, client]);

  async function selectSuggestion(s: PlaceSuggestion) {
    const session = sessionRef.current ?? new PlacesSession();
    setOpen(false);
    setLoading(true);
    setSuppress(true); // the fill sets value.street — don't re-query it
    try {
      const addr = await session.finalize(client, s.placeId);
      onChange(addr); // fills all four; every field stays editable
    } catch {
      // Details failed — keep the typed suggestion text; still no throw.
      onChange({ ...value, street: s.primaryText });
    } finally {
      setLoading(false);
      sessionRef.current = null; // session consumed → next one is fresh
      setSuggestions(null);
    }
  }

  function handleStreetChange(next: string) {
    setSuppress(false); // user is typing again → allow queries
    onChange({ ...value, street: next });
  }

  function enterManually() {
    setManual(true);
    setOpen(false);
    setSuggestions(null);
    sessionRef.current = null;
  }

  return (
    <div className="flex flex-col gap-1.5">
      <TextField
        label="Street"
        value={value.street}
        onChange={(e) => handleStreetChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          // Delay the close so a suggestion click (which we keep focus for via
          // onMouseDown) still registers before the dropdown unmounts.
          window.setTimeout(() => setOpen(false), 150);
        }}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        required
      />
      {!manual && open && (
        <div className="flex flex-col gap-1">
          {loading && (
            <p role="status" className="px-1 text-xs text-brk-dim">
              Searching…
            </p>
          )}
          {suggestions && suggestions.length > 0 && (
            <ul
              role="listbox"
              aria-label="Address suggestions"
              className="flex flex-col overflow-hidden rounded-md border border-brk-border"
            >
              {suggestions.map((s) => (
                <li
                  key={s.placeId}
                  className="border-t border-brk-border first:border-t-0"
                >
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    // Keep focus so onBlur doesn't close the list before onClick.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => void selectSuggestion(s)}
                    className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-brk-bg"
                  >
                    <span className="text-brk-text">{s.primaryText}</span>
                    {s.secondaryText && (
                      <span className="text-xs text-brk-dim">
                        {s.secondaryText}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {suggestions && suggestions.length === 0 && !loading && (
            <p className="px-1 text-xs text-brk-dim">
              No matches — keep typing, or enter it manually below.
            </p>
          )}
        </div>
      )}
      {!manual && (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={enterManually}
          // Muted, not accent (BRK-S091): the brand accent is red, so an
          // accent-coloured line sitting under an address input read as a
          // validation message rather than the secondary action it is.
          className="self-start text-xs text-brk-dim underline decoration-dotted hover:text-brk-text"
        >
          Enter address manually
        </button>
      )}
    </div>
  );
}
